'use client'

import { ext } from '@/utils/linkTarget'
import { useEffect, useRef, useState } from 'react'
import Image from 'next/image'
import HeaderGap from '@/components/atoms/headerGap'
import Container from '@/components/templates/container'
import Layout from '@/components/templates/layout'
import Spacer from '@/components/atoms/spacer'
import RectangleButton from '@/components/molecules/rectangleButton'
import TestimonialsSection from '@/components/organisms/testimonialsSection'
import ClassesSection from '@/components/organisms/classesSection'
import Community from '@/components/organisms/community'
import { urlForImage } from '@/sanity/lib/image'
import { clientFetch } from '@/sanity/lib/client'
import { getFileAsset } from '@sanity/asset-utils'
import getYoutube from '@/utils/getYoutube'
import titleCase from '@/utils/titleCase'
import sentence from '@/utils/sentenceCase'
import TodayAtMahima from '../../../app/home/components/todayAtMahima'

export const PLAYTOMIC = 'https://playtomic.com/clubs/mahima'

export const body = 'font-chivoMono text-bodyMobile md:text-bodyDesktop text-umber'
export const h2 = 'font-ttDrugsTrial text-olive text-[30px] md:text-[40px] leading-none'
export const solidBtn =
  'inline-block self-start px-7 py-4 bg-forest text-background font-chivoMono text-[13px] uppercase tracking-widest hover:bg-umber transition-colors duration-300'
export const outlineBtn =
  'inline-block self-start px-7 py-4 border border-olive text-olive font-chivoMono text-[13px] uppercase tracking-widest hover:bg-olive hover:text-background transition-colors duration-300'

// Same-tab for pages on mahimabali.com, new tab for booking apps and other sites (utils/linkTarget)
export { ext }

// Sanity body text: blank lines split paragraphs, lines starting with "- " become a list
export const RichText = ({ text, className = '' }) => {
  if (!text) return null
  const blocks = text.split(/\n\s*\n/).map((b) => b.trim()).filter(Boolean)
  const out = []
  let list = []
  const flush = () => {
    if (list.length) out.push({ type: 'list', items: list })
    list = []
  }
  for (const b of blocks) {
    if (b.startsWith('- ')) list.push(b.slice(2).replace(/\s*\n\s*/g, ' '))
    else {
      flush()
      out.push({ type: 'p', text: b })
    }
  }
  flush()
  return (
    <div className={`flex flex-col gap-4 ${className}`}>
      {out.map((blk, i) =>
        blk.type === 'p' ? (
          <p key={i} className={body}>{blk.text}</p>
        ) : (
          <ul key={i} className="flex flex-col border-b border-dune">
            {blk.items.map((item, j) => (
              <li key={j} className={`${body} border-t border-dune py-3`}>{item}</li>
            ))}
          </ul>
        ),
      )}
    </div>
  )
}

// image: a Sanity image, or a plain URL (e.g. local camp photos)
export const Photo = ({ image, alt, className = '', position = 'center' }) => (
  <div className={`relative overflow-hidden bg-dune ${className}`}>
    {typeof image === 'string' && image && (
      <Image src={image} alt={alt} fill className="object-cover" style={{ objectPosition: position }} />
    )}
    {image?.asset && (
      <Image
        src={urlForImage(image).url()}
        alt={image.alt || alt}
        fill
        placeholder={image.metadata?.lqip ? 'blur' : 'empty'}
        blurDataURL={image.metadata?.lqip}
        className="object-cover"
        style={{ objectPosition: position }}
      />
    )}
  </div>
)

// ── Hero ───────────────────────────────────────────────────────────────────

const HeroVideo = ({ video, poster }) => {
  const ref = useRef(null)
  const src = getFileAsset(video, clientFetch.config()).url
  useEffect(() => { ref.current?.play() }, [src])
  return (
    <video
      ref={ref}
      playsInline
      muted
      loop
      autoPlay
      poster={poster ? urlForImage(poster).url() : undefined}
      className="absolute inset-0 w-full h-full object-cover"
    >
      <source src={src} type="video/mp4" />
    </video>
  )
}

export const Hero = ({ cover, hero, cta }) => {
  const firstImage = cover?.images?.[0]?.image
  const label = [cover?.title?.rowOne, cover?.title?.rowTwo].filter(Boolean).join(' ')
  return (
    <section className="w-full px-smallMobile md:px-mediumDesktop pt-6 lg:pt-10">
      <div className="relative max-w-container mx-auto h-[70svh] min-h-[520px] lg:h-[760px] overflow-hidden bg-forest flex items-end">
        {cover?.option === 'video' && cover.video?.asset ? (
          <HeroVideo video={cover.video} poster={cover.imagePlaceholder} />
        ) : firstImage?.asset ? (
          <Image src={urlForImage(firstImage).url()} alt={firstImage.alt || 'Padel at Mahima, Ubud'} fill priority className="object-cover" />
        ) : null}
        <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-black/5" />
        <div className="relative w-full p-8 md:p-12 lg:p-16 flex flex-col md:flex-row md:items-end md:justify-between gap-8 text-background">
          <div>
            {label && <p className="font-chivoMono text-[12px] uppercase tracking-widest text-dune mb-5">{titleCase(label)}</p>}
            <h1 className="font-ttDrugsTrial text-[44px] md:text-[64px] lg:text-[80px] leading-[0.95]">{titleCase(hero.heading)}</h1>
            {hero.subheading && <p className="font-chivoMono text-[15px] md:text-[17px] text-dune mt-5">{sentence(hero.subheading)}</p>}
          </div>
          <a href={cta.link} {...ext(cta.link)}
            className="shrink-0 self-start md:self-auto px-7 py-4 bg-background text-olive font-chivoMono text-[13px] uppercase tracking-widest hover:bg-dune transition-colors duration-300">
            {titleCase(cta.name)}
          </a>
        </div>
      </div>
    </section>
  )
}

// ── Content sections (image + text, alternating) ──────────────────────────

export const Feature = ({ section, imageLeft, solid }) => (
  <Container outerClassName="mt-[100px] lg:mt-[150px]">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-20 items-center">
      <Photo
        image={section.image}
        alt={`${titleCase(section.heading)} at Mahima, Ubud`}
        className={`aspect-[4/5] ${imageLeft ? 'lg:order-1' : 'lg:order-2'}`}
      />
      <div className={`flex flex-col gap-8 lg:gap-10 ${imageLeft ? 'lg:order-2' : 'lg:order-1'}`}>
        {section.heading && <h2 className={h2}>{titleCase(section.heading)}</h2>}
        <RichText text={section.body} className="max-w-[540px]" />
        {section.ctaButton?.name && section.ctaButton?.link && (
          <a href={section.ctaButton.link} {...ext(section.ctaButton.link)} className={`mt-2 ${solid ? solidBtn : outlineBtn}`}>
            {titleCase(section.ctaButton.name)}
          </a>
        )}
      </div>
    </div>
  </Container>
)

// ── Coaches ────────────────────────────────────────────────────────────────

// One profile card per coach: identity first, then what they coach
export const CoachProfile = ({ coach, focusLabel = 'Skills & Focus', bookUrl, bookLabel }) => {
  const first = coach.name?.split(' ')[0]
  // Some role fields hold a "•" list of credentials: show those as tags instead of a role
  const rawRole = coach.position?.trim() || ''
  const credentials = rawRole.includes('•') ? rawRole.split('•').map((c) => c.trim()).filter(Boolean) : []
  const role = credentials.length ? 'Coach' : rawRole
  // Drop bio lines that only repeat the skill list ("Focus: …")
  const bios = [coach.bio || coach.description, coach.bio2].filter((t) => t && !/^\s*focus\s*:/i.test(t))
  const skills = [...credentials, ...(coach.focusAreas || [])]
  return (
    <article className="border border-dune p-6 lg:p-10 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-stretch">
      {/* Left: photo, as tall as the content beside it on desktop */}
      <Photo image={coach.photo} alt={`${coach.name}, ${role || 'coach'} at Mahima`}
        className="aspect-[4/3] lg:aspect-auto lg:h-full lg:min-h-[360px] w-full" position="center 25%" />

      {/* Right: name and booking, about, then what they focus on */}
      <div className="flex flex-col gap-8">
        <div className="flex flex-col gap-3">
          {role && <span className="font-chivoMono text-[11px] uppercase tracking-widest text-khaki">{role}</span>}
          <h3 className="font-ttDrugsTrial text-olive text-[30px] md:text-[40px] leading-none">{coach.name}</h3>
          {bookUrl && (
            <a href={bookUrl} {...ext(bookUrl)}
              className="self-start mt-2 pb-1 border-b border-olive font-chivoMono text-[12px] uppercase tracking-widest text-olive hover:text-umber hover:border-umber transition-colors duration-200">
              {bookLabel || `Book with ${first}`} <span aria-hidden="true">→</span>
            </a>
          )}
        </div>
        {bios.length > 0 && (
          <div className="flex flex-col gap-4">
            <p className="font-chivoMono text-[11px] uppercase tracking-widest text-khaki">About</p>
            {bios.map((t, i) => <p key={i} className={body}>{t}</p>)}
          </div>
        )}
        {skills.length > 0 && (
          <div className={`flex flex-col gap-4 ${bios.length ? 'pt-8 border-t border-dune' : ''}`}>
            <p className="font-chivoMono text-[11px] uppercase tracking-widest text-khaki">{focusLabel}</p>
            <ul className="flex flex-wrap gap-2">
              {skills.map((skill) => (
                <li key={skill} className="px-3 py-2 border border-khaki/40 font-chivoMono text-[12px] text-olive">{skill}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </article>
  )
}

export const Coaches = ({ coaches, heading, focusLabel, bookUrl, bookLabel }) => {
  if (!coaches?.length) return null
  return (
    <Container outerClassName="mt-[100px] lg:mt-[150px]">
      <section data-nosnippet>
        <h2 className={`${h2} mb-10 lg:mb-12`}>{titleCase(heading || 'Coaches')}</h2>
        <div className="flex flex-col gap-4">
          {coaches.map((coach) => (
            <CoachProfile key={coach.name} coach={coach} focusLabel={focusLabel} bookUrl={bookUrl} bookLabel={bookLabel} />
          ))}
        </div>
      </section>
    </Container>
  )
}

// Forest band with horizontal coach cards (padel, tennis and gym pages)
export const CoachesBand = ({ coaches, heading }) => {
  if (!coaches?.length) return null
  return (
    <section className="w-full bg-forest text-background px-smallMobile md:px-mediumDesktop py-[90px] lg:py-[130px] mt-[100px] lg:mt-[150px]" data-nosnippet>
      <div className="max-w-container mx-auto">
        <h2 className="font-ttDrugsTrial text-[30px] md:text-[40px] leading-none mb-12 lg:mb-14">{titleCase(heading || 'Coaches')}</h2>
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {coaches.map((coach) => (
            <div key={coach.name} className="flex flex-col sm:flex-row gap-6 lg:gap-8 border border-background/20 p-6 lg:p-8">
              <Photo
                image={coach.photo}
                alt={`${coach.name}, ${coach.position || 'coach'} at Mahima`}
                className="aspect-[4/5] w-full sm:w-[240px] lg:w-[280px] shrink-0 bg-umber"
                position="center 25%"
              />
              <div className="flex flex-col gap-4">
                {coach.position && <span className="font-chivoMono text-[11px] uppercase tracking-widest text-dune">{coach.position}</span>}
                <h3 className="font-ttDrugsTrial text-[28px] md:text-[32px] leading-none">{coach.name}</h3>
                {coach.description && <p className="font-chivoMono text-bodyMobile md:text-bodyDesktop text-background/75 mt-2">{coach.description}</p>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ── Video ──────────────────────────────────────────────────────────────────

export const VideoSection = ({ videoSection }) => {
  if (!videoSection) return null
  const { heading, option, youtubeUrl, videoFile } = videoSection
  const youtubeId = option === 'youtube' && youtubeUrl ? getYoutube(youtubeUrl) : null
  const uploadUrl = option === 'upload' ? videoFile?.asset?.url : null
  if (!youtubeId && !uploadUrl) return null
  return (
    <Container outerClassName="mt-[100px] lg:mt-[150px]">
      {heading && <h2 className={`${h2} mb-12`}>{titleCase(heading)}</h2>}
      <div className="relative w-full aspect-video bg-black">
        {youtubeId && (
          <iframe src={`https://www.youtube.com/embed/${youtubeId}?rel=0&modestbranding=1`}
            title={heading || 'Video'} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen className="absolute inset-0 w-full h-full border-0" />
        )}
        {uploadUrl && <video src={uploadUrl} controls className="absolute inset-0 w-full h-full object-cover" playsInline />}
      </div>
    </Container>
  )
}

// ── Pricing ────────────────────────────────────────────────────────────────

const tabPill = (active) =>
  `min-h-[44px] px-5 py-2.5 border font-chivoMono text-[12px] uppercase tracking-widest whitespace-nowrap transition-colors duration-200 ${
    active ? 'bg-forest border-forest text-background' : 'border-khaki/40 text-umber hover:border-forest'
  }`

export const Pricing = ({ data, fallbackTitle = 'Prices', id = 'pricing' }) => {
  const [active, setActive] = useState(0)
  const hasTabs = data?.tabs?.length > 0
  const rates = hasTabs ? data.tabs[active]?.rates ?? [] : data?.rates ?? []
  const note = hasTabs ? data.tabs[active]?.note : data?.note
  if (!hasTabs && !data?.rates?.length && !data?.description) return null

  return (
    <Container outerClassName="mt-[100px] lg:mt-[150px]">
      <section id={id} className="scroll-mt-[100px] grid grid-cols-1 lg:grid-cols-[2fr_3fr] gap-10 lg:gap-20">
        <div className="flex flex-col gap-8 lg:gap-10">
          <h2 className={h2}>{titleCase(data.title || fallbackTitle)}</h2>
          {data.description && <p className={`${body} max-w-[460px]`}>{data.description}</p>}
        </div>
        <div>
          {hasTabs && (
            <div role="tablist" aria-label="Price categories" className="flex gap-2 mb-7 overflow-x-auto scrollbar-hidden">
              {data.tabs.map((tab, i) => (
                <button key={i} type="button" role="tab" aria-selected={active === i} onClick={() => setActive(i)} className={tabPill(active === i)}>
                  {tab.tabLabel}
                </button>
              ))}
            </div>
          )}
          <div className="border-t border-olive">
            {rates.map((r, i) => (
              <div key={i} className="flex items-baseline justify-between gap-6 py-5 border-b border-dune">
                <span className="font-chivoMono text-bodyMobile md:text-bodyDesktop text-olive">{r.label}</span>
                <span className="font-chivoMono text-bodyMobile md:text-bodyDesktop text-umber whitespace-nowrap">{r.price}</span>
              </div>
            ))}
          </div>
          {note && <p className="font-chivoMono text-[13px] text-khaki mt-4">{note}</p>}
          <div className="flex flex-wrap items-center gap-3 mt-8">
            <a href={PLAYTOMIC} {...ext(PLAYTOMIC)} className={solidBtn}>Book now</a>
            <a href={data.whatsappLink || 'https://wa.me/6281337582672'} {...ext(data.whatsappLink || 'https://wa.me/6281337582672')} className={outlineBtn}>
              Book on WhatsApp
            </a>
            {data.rezervLink && (
              <a href={data.rezervLink} {...ext(data.rezervLink)}
                className="pb-1 border-b border-olive font-chivoMono text-olive text-[13px] uppercase tracking-widest hover:text-umber">
                {data.rezervLabel || 'Book via Rezerv'}
              </a>
            )}
          </div>
        </div>
      </section>
    </Container>
  )
}

// ── FAQ ────────────────────────────────────────────────────────────────────

export const FaqItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false)
  return (
    <div className="border-t border-dune">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
        className="w-full flex items-start justify-between gap-6 py-6 text-left">
        <h3 className="font-ttDrugsTrial text-olive text-[20px] md:text-[22px] leading-snug">{question}</h3>
        <span className="font-ttDrugsTrial text-khaki text-[22px] leading-none shrink-0">{open ? '−' : '+'}</span>
      </button>
      <div className={`grid transition-all duration-300 ease-in-out ${open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
        <div className="overflow-hidden">
          <p className={`${body} pb-6 max-w-[640px]`}>{answer}</p>
        </div>
      </div>
    </div>
  )
}

// ── Intro, gallery, FAQ ────────────────────────────────────────────────────

// First intro paragraph as a large statement on the left, the rest on the right
export const Intro = ({ intro, showPricingLink }) => {
  const [statement, ...rest] = (intro || '').split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean)
  if (!statement) return null
  return (
    <Container outerClassName="mt-[100px] lg:mt-[140px]">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-20">
        <p className="font-ttDrugsTrial text-olive text-[26px] md:text-[34px] leading-snug">{statement}</p>
        <div className="flex flex-col gap-5">
          {rest.map((p, i) => <p key={i} className={body}>{p}</p>)}
          {showPricingLink && (
            <a href="#pricing" className="self-start mt-2 pb-1 border-b border-olive font-chivoMono text-olive text-[13px] uppercase tracking-widest hover:text-umber">
              Prices &amp; memberships
            </a>
          )}
        </div>
      </div>
    </Container>
  )
}

// Four photos in a row, every second one dropped
export const StaggeredGallery = ({ photos = [], alt }) => {
  if (!photos.length) return null
  return (
    <section aria-label="Gallery" className="w-full px-smallMobile md:px-mediumDesktop mt-[80px] lg:mt-[110px]">
      <div className="max-w-container mx-auto grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
        {photos.slice(0, 4).map((photo, i) => (
          <Photo key={i} image={photo} alt={alt} className={`aspect-[4/5] ${i % 2 ? 'mt-10 lg:mt-14' : ''}`} />
        ))}
      </div>
    </section>
  )
}

export const FaqSection = ({ faq = [] }) => {
  if (!faq.length) return null
  return (
    <Container outerClassName="mt-[100px] lg:mt-[130px]">
      <div className="grid grid-cols-1 lg:grid-cols-[2fr_3fr] gap-8 lg:gap-20">
        <h2 className={h2}>FAQ</h2>
        <div>
          {faq.map((item, i) => <FaqItem key={i} question={item.question} answer={item.answer} />)}
          <div className="border-t border-dune" />
        </div>
      </div>
    </Container>
  )
}

// ── Full sport page (padel, tennis) ────────────────────────────────────────

// sport: 'PADEL' | 'TENNIS'; pricing: that sport's pricing object from Sanity
const SportPage = ({ sport, pageAPI, testimonials, pricing, footerAPI, settingAPI }) => {
  const name = sport === 'TENNIS' ? 'Tennis' : 'Padel'
  const hero = pageAPI?.hero || {}
  const sections = pageAPI?.sections || []
  const heroCta = sections[0]?.ctaButton?.link
    ? sections[0].ctaButton
    : { name: `Book ${name.toLowerCase()} court`, link: PLAYTOMIC }

  return (
    <Layout settingAPI={settingAPI} footerAPI={footerAPI}>
      <HeaderGap />
      <Hero cover={pageAPI?.cover} hero={hero} cta={heroCta} />
      <Intro intro={hero.intro} showPricingLink={!pageAPI?.hidePricing} />
      <StaggeredGallery photos={pageAPI?.photos} alt={`${name} at Mahima, Ubud`} />

      {sections[0] && <Feature section={sections[0]} imageLeft solid />}

      <TodayAtMahima sport={sport} showOpenMatches={false} heading="On the Courts" variant="cards" />

      {sections.slice(1).map((s, i) => (
        <Feature key={i} section={s} imageLeft={i % 2 === 1} />
      ))}

      {(pageAPI?.ctaHeading || pageAPI?.ctaDescription) && (
        <Container outerClassName="mt-[100px] lg:mt-[150px]">
          <Spacer />
          <div className="flex flex-col gap-smallMobile mt-smallMobile lg:mt-smallDesktop">
            {pageAPI.ctaHeading && <h2 className={h2}>{titleCase(pageAPI.ctaHeading)}</h2>}
            {pageAPI.ctaDescription && <p className={`${body} max-w-[600px]`}>{pageAPI.ctaDescription}</p>}
            <div className="flex flex-wrap gap-smallMobile mt-smallMobile">
              <RectangleButton name="Book a Court" link="/book-join" />
            </div>
          </div>
        </Container>
      )}

      {!pageAPI?.hideClasses && (
        <>
          <div id="classes" />
          <ClassesSection classes={pageAPI?.classes} heading={pageAPI?.classesHeading} />
        </>
      )}

      <div id="coaches" />
      <CoachesBand coaches={pageAPI?.coaches} heading={pageAPI?.coachesHeading} />

      <VideoSection videoSection={pageAPI?.videoSection} />

      {!pageAPI?.hidePricing && <Pricing data={pricing} fallbackTitle={`${name} courts & lessons`} />}

      <div id="testimonials" className="mt-[100px] lg:mt-[150px]" />
      <TestimonialsSection testimonials={testimonials} googleReviewUrl={settingAPI?.googleReviewUrl}
        reviewCtaText={settingAPI?.reviewCtaText} heading={pageAPI?.testimonialsHeading} band />

      <FaqSection faq={pageAPI?.faq} />

      <Community title={settingAPI?.community?.title} description={settingAPI?.community?.description}
        image={settingAPI?.community?.image} ctaButton={settingAPI?.community?.ctaButton} />
    </Layout>
  )
}

export default SportPage
